import { ArrowRight, TrendingUp } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SERVER_URL } from "../api/client";
import { PageHeader, SectionHeading } from "../components/layout/PageHeader";
import { tierColor, tierLabel } from "../constants/tiers";
import { useAuth } from "../context/AuthContext";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface RecentMerchant {
	id: number;
	name: string;
	website: string;
	tier: string;
	validated: boolean;
	createdAt: string;
}
interface RecentCustomer {
	id: number;
	name: string;
	email: string;
	verified: boolean;
	merchantName: string | null;
	createdAt: string;
}
interface ClickPoint {
	day: string;
	impressions: number;
	clicks: number;
}
interface Stats {
	merchants: {
		total: number;
		validated: number;
		unvalidated: number;
		paid: number;
		newLast30d: number;
	};
	affiliates: number;
	affiliatesValidated: number;
	trialing: number;
	tiers: {
		AFFILIATE: number;
		PAYG: number;
		SUB: number;
		PCSS: number;
		PAYGCSS: number;
		SUBCSS: number;
		BUSINESS: number;
		BUSINESSCSS: number;
		FOUNDER: number;
		STARTUP: number;
	};
	customers: {
		total: number;
		verified: number;
		unverified: number;
		newLast30d: number;
	};
	clicks: { totalClicks: number; totalImpressions: number; ctr: number };
	clicksLast30d: { clicks: number; impressions: number; ctr: number };
	clicksChart: ClickPoint[];
	recentMerchants: RecentMerchant[];
	recentCustomers: RecentCustomer[];
}

const fmt = (n: number) => n.toLocaleString();
const fmtDate = (s: string) =>
	new Date(s).toLocaleDateString("da-DK", {
		day: "2-digit",
		month: "short",
		year: "numeric",
	});

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------
const Dashboard = () => {
	const { user } = useAuth();
	const [stats, setStats] = useState<Stats | null>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		(async () => {
			try {
				const res = await fetch(`${SERVER_URL}/stats`, {
					credentials: "include",
				});
				if (res.ok) setStats((await res.json()) as Stats);
			} catch {
				// ignore
			} finally {
				setLoading(false);
			}
		})();
	}, []);

	if (loading) {
		return (
			<div className="flex items-center justify-center py-32">
				<div className="h-8 w-8 animate-spin rounded-full border-3 border-zinc-200 border-t-zinc-900" />
			</div>
		);
	}

	if (!stats) {
		return (
			<div className="flex flex-col items-center justify-center py-32 text-slate-400">
				<TrendingUp className="w-12 h-12 mb-3 text-slate-300" />
				<p className="font-semibold text-slate-500">Couldn't load stats</p>
			</div>
		);
	}

	const statTiles = [
		{
			label: "Active merchants",
			value: fmt(stats.merchants.validated),
			unit: `/ ${fmt(stats.merchants.total)}`,
			sub: `+${fmt(stats.merchants.newLast30d)} in 30 days`,
			to: "/merchants",
		},
		{
			label: "Active affiliates",
			value: fmt(stats.affiliatesValidated),
			unit: `/ ${fmt(stats.affiliates)}`,
			sub: "Partner-ads · Adtraction",
			to: "/merchants",
		},
		{
			label: "Customers",
			value: fmt(stats.customers.total),
			unit: "",
			sub: `${fmt(stats.customers.verified)} verified`,
			to: "/customers",
		},
		{
			label: "Total clicks",
			value: fmt(stats.clicks.totalClicks),
			unit: "",
			sub: `${stats.clicks.ctr}% CTR all time`,
			to: "/products",
		},
	];

	const tierEntries = [
		{ key: "PAYG", value: stats.tiers.PAYG },
		{ key: "SUB", value: stats.tiers.SUB },
		{ key: "PCSS", value: stats.tiers.PCSS },
		{ key: "PAYGCSS", value: stats.tiers.PAYGCSS },
		{ key: "SUBCSS", value: stats.tiers.SUBCSS },
		{ key: "BUSINESS", value: stats.tiers.BUSINESS },
		{ key: "BUSINESSCSS", value: stats.tiers.BUSINESSCSS },
		{ key: "FOUNDER", value: stats.tiers.FOUNDER },
		{ key: "AFFILIATE", value: stats.tiers.AFFILIATE },
		{ key: "STARTUP", value: stats.tiers.STARTUP },
	];
	const tierMax = Math.max(...tierEntries.map((t) => t.value), 1);

	// One scale for both series. Impressions always dominate clicks, so scaling
	// each to its own max drew the clicks bar taller than the impressions bar it
	// sits inside — a chart that claimed more clicks than impressions.
	const chartMax = Math.max(
		...stats.clicksChart.map((d) => Math.max(d.impressions, d.clicks)),
		1,
	);
	const lastDay = stats.clicksChart.length - 1;
	const today = new Date().toLocaleDateString("en-GB", {
		day: "numeric",
		month: "short",
		year: "numeric",
	});

	return (
		<div className="space-y-8">
			<PageHeader
				kicker={`Overview · Platform · ${today}`}
				title={`Welcome back, ${user?.name?.split(" ")[0] || "Admin"}.`}
				qualifier="Here is the platform today."
				description="Merchants, customers and click performance across priceonline.dk."
				actions={
					<>
						<Link
							to="/flows/approvals"
							className="rounded-full bg-brand px-4 py-2 font-semibold text-[13px] text-black transition-colors hover:bg-brand-hover"
						>
							Review approvals
						</Link>
						<Link
							to="/merchants"
							className="rounded-full border border-zinc-200 px-4 py-2 font-medium text-[13px] text-zinc-700 transition-colors hover:border-zinc-300"
						>
							Merchants
						</Link>
					</>
				}
			/>

			<div className="card grid grid-cols-1 divide-y divide-zinc-200 bg-white sm:grid-cols-2 sm:divide-y-0 xl:grid-cols-4 xl:divide-x">
				{statTiles.map((tile) => (
					<Link
						key={tile.label}
						to={tile.to}
						className="group p-6 transition-colors hover:bg-zinc-50/70"
					>
						<p className="font-medium text-[0.7rem] text-zinc-500 uppercase tracking-[0.14em]">
							{tile.label}
						</p>
						<p className="tnum mt-3 font-semibold text-[30px] text-ink leading-none">
							{tile.value}{" "}
							{tile.unit && (
								<span className="font-normal text-[15px] text-zinc-400">
									{tile.unit}
								</span>
							)}
						</p>
						<p className="mt-3 flex items-center gap-1.5 text-[12px] text-zinc-500">
							{tile.sub}
							<ArrowRight className="h-3.5 w-3.5 text-zinc-400 transition-transform duration-200 group-hover:translate-x-1" />
						</p>
					</Link>
				))}
			</div>

			<div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
				<div className="card bg-white p-6 xl:col-span-2">
					<SectionHeading
						kicker="Clicks · Last 30 days"
						title={`${fmt(stats.clicksLast30d.clicks)} clicks.`}
						qualifier={`From ${fmt(stats.clicksLast30d.impressions)} impressions.`}
						actions={
							<div className="flex gap-5 text-[11px] text-zinc-500 uppercase tracking-[0.14em]">
								<span className="flex items-center gap-2">
									<span className="h-2.5 w-2.5 rounded-sm bg-zinc-900" />
									Clicks
								</span>
								<span className="flex items-center gap-2">
									<span className="h-2.5 w-2.5 rounded-sm bg-zinc-200" />
									Impressions
								</span>
							</div>
						}
					/>

					<div className="mt-6 flex h-56 items-end gap-1">
						{stats.clicksChart.map((d, i) => (
							<div
								key={d.day}
								className="group/bar flex h-full flex-1 flex-col items-center justify-end"
							>
								<div className="relative flex h-full w-full cursor-pointer items-end justify-center">
									<div className="pointer-events-none absolute -top-1 left-1/2 z-10 -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-md bg-zinc-900 px-2 py-1 font-medium text-[10px] text-white opacity-0 transition-opacity group-hover/bar:opacity-100">
										{d.day}: {fmt(d.clicks)} clicks · {fmt(d.impressions)} impr.
									</div>
									<div
										className="w-full max-w-4 rounded-t-[3px] bg-zinc-200 transition-colors duration-200 group-hover/bar:bg-zinc-300"
										style={{
											height: `${Math.max((d.impressions / chartMax) * 100, d.impressions > 0 ? 1 : 0)}%`,
										}}
									/>
									<div
										className={`absolute bottom-0 left-1/2 w-full max-w-2 -translate-x-1/2 rounded-t-[3px] ${
											i === lastDay ? "bg-brand" : "bg-zinc-900"
										}`}
										style={{
											height: `${Math.max((d.clicks / chartMax) * 100, d.clicks > 0 ? 1 : 0)}%`,
										}}
									/>
								</div>
								{i % 5 === 0 && (
									<span className="mt-1.5 whitespace-nowrap text-[9px] text-zinc-400 uppercase tracking-[0.1em]">
										{d.day.slice(5)}
									</span>
								)}
							</div>
						))}
					</div>

					{stats.clicksLast30d.impressions === 0 && (
						<p className="mt-3 text-center text-[11px] text-zinc-400">
							Nothing recorded in the last 30 days. All time:{" "}
							{fmt(stats.clicks.totalClicks)} clicks over{" "}
							{fmt(stats.clicks.totalImpressions)} impressions.
						</p>
					)}
				</div>

				<div className="card bg-white p-6">
					<SectionHeading
						kicker="Merchant tiers"
						title={`${fmt(stats.merchants.paid)} paying.`}
						qualifier={`${fmt(stats.affiliates)} affiliates.`}
					/>

					{/* Trialing is a subscription state (status='trialing'), not a tier —
					    these merchants also appear under their chosen paid tier below. */}
					<div className="mt-5 border-brand border-l-2 pl-4">
						<p className="text-[0.7rem] text-zinc-500 uppercase tracking-[0.14em]">
							On trial
						</p>
						<p className="tnum font-semibold text-[22px] text-ink">
							{fmt(stats.trialing)}{" "}
							<span className="font-normal text-[13px] text-zinc-400">
								merchants
							</span>
						</p>
					</div>

					<div className="mt-4 divide-y divide-zinc-100">
						{tierEntries.map((t) => (
							<div key={t.key} className="flex items-center gap-3 py-2.5">
								<span className="w-36 shrink-0 truncate text-[13px] text-zinc-700">
									{tierLabel(t.key)}
								</span>
								<div className="h-1 flex-1 rounded-full bg-zinc-100">
									<div
										className="h-1 rounded-full bg-zinc-900"
										style={{ width: `${(t.value / tierMax) * 100}%` }}
									/>
								</div>
								<span className="tnum w-10 text-right font-semibold text-[13px] text-ink">
									{fmt(t.value)}
								</span>
							</div>
						))}
					</div>
				</div>
			</div>

			<div className="grid grid-cols-1 gap-10 border-zinc-200 border-t pt-8 xl:grid-cols-2">
				<section>
					<SectionHeading
						kicker="Latest sign-ups"
						title="Recent merchants."
						qualifier={`${fmt(stats.merchants.newLast30d)} in 30 days.`}
						actions={
							<Link
								to="/merchants"
								className="group flex items-center gap-1.5 font-medium text-[13px] text-ink"
							>
								All merchants
								<ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
							</Link>
						}
					/>
					<div className="divide-y divide-zinc-100">
						{stats.recentMerchants.map((m) => (
							<div
								key={m.id}
								className="grid grid-cols-12 items-center gap-3 py-3 text-[13px]"
							>
								<Link
									to={`/merchants/${m.id}`}
									className="col-span-4 truncate font-medium text-ink hover:underline"
								>
									{m.name}
								</Link>
								<span className="col-span-3">
									<span
										className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-0.5 font-medium text-[11px] ${tierColor(m.tier)}`}
									>
										{tierLabel(m.tier)}
									</span>
								</span>
								<span className="col-span-3 flex items-center gap-2 text-zinc-600">
									<span
										className={`size-1.5 rounded-full ${m.validated ? "bg-emerald-500" : "bg-red-500"}`}
									/>
									{m.validated ? "Listed" : "Not listed"}
								</span>
								<span className="col-span-2 text-right text-[12px] text-zinc-400">
									{fmtDate(m.createdAt)}
								</span>
							</div>
						))}
					</div>
				</section>

				<section>
					<SectionHeading
						kicker="Latest sign-ups"
						title="Recent customers."
						qualifier={`${fmt(stats.customers.newLast30d)} in 30 days.`}
						actions={
							<Link
								to="/customers"
								className="group flex items-center gap-1.5 font-medium text-[13px] text-ink"
							>
								All customers
								<ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
							</Link>
						}
					/>
					<div className="divide-y divide-zinc-100">
						{stats.recentCustomers.map((c) => (
							<div
								key={c.id}
								className="grid grid-cols-12 items-center gap-3 py-3 text-[13px]"
							>
								<div className="col-span-5 min-w-0">
									<p className="truncate font-medium text-ink">{c.name}</p>
									<p className="truncate text-[11px] text-zinc-400">
										{c.email}
									</p>
								</div>
								<span className="col-span-4 truncate text-zinc-500">
									{c.merchantName ?? "–"}
								</span>
								<span className="col-span-3 flex items-center justify-end gap-2 text-zinc-600">
									<span
										className={`size-1.5 rounded-full ${c.verified ? "bg-emerald-500" : "bg-zinc-300"}`}
									/>
									{c.verified ? "Verified" : "Unverified"}
								</span>
							</div>
						))}
					</div>
				</section>
			</div>
		</div>
	);
};

export default Dashboard;
